import React from "react";
import { CONSENT_COOKIE } from "../../GoogleAnalytics";

// Defined by the gtag snippet in the document head, and only in production — every read is
// guarded with `typeof`, matching how grecaptcha is handled.
declare var gtag: (command: string, action: string, params: Record<string, string>) => void;

const CONSENT_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export type TConsentChoice = "granted" | "denied";

export const readConsent = (cookie: string): TConsentChoice | null =>
    cookie.split("; ").reduce<TConsentChoice | null>((found, pair) => {
        if (found !== null) {
            return found;
        }
        if (pair === `${CONSENT_COOKIE}=granted`) {
            return "granted";
        }
        if (pair === `${CONSENT_COOKIE}=denied`) {
            return "denied";
        }
        return null;
    }, null);

export const consentCookie = (choice: TConsentChoice): string =>
    `${CONSENT_COOKIE}=${choice}; path=/; max-age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax`;

export const CookieConsent = (): JSX.Element | null => {
    const [choice, setChoice] = React.useState<TConsentChoice | null | "unknown">("unknown");

    React.useEffect(() => {
        setChoice(readConsent(document.cookie));
    }, []);

    const decide = (next: TConsentChoice) => () => {
        document.cookie = consentCookie(next);
        if (next === "granted" && typeof gtag !== "undefined") {
            gtag("consent", "update", { analytics_storage: "granted" });
        }
        setChoice(next);
    };

    if (choice === "unknown" || choice !== null) {
        return null;
    }

    return (
        <aside className="cookieConsent" role="dialog" aria-label="Cookies">
            <div className="cookieConsent__inner">
                <p className="cookieConsent__text">
                    We use cookies to see how people find and use this site. Nothing is stored
                    until you say yes, and we never sell your data.{" "}
                    <a className="cookieConsent__link" href="/compliance/privacy-policy">
                        Read our privacy policy
                    </a>.
                </p>
                <div className="cookieConsent__actions">
                    <button className="cookieConsent__button cookieConsent__button--secondary" onClick={decide("denied")}>
                        No thanks
                    </button>
                    <button className="cookieConsent__button cookieConsent__button--primary" onClick={decide("granted")}>
                        That's fine
                    </button>
                </div>
            </div>
        </aside>
    );
};
